import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaSearch, FaEye, FaEllipsisV, FaDownload, FaCloudUploadAlt, FaChevronRight,
} from "react-icons/fa";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import EmptyState from "../Components/States/EmptyState";
import MedicalRecordsEmptyState from "../Components/States/MedicalRecordsEmptyState";
import OfflineState from "../Components/States/OfflineState";
import LoadingState from "../Components/States/LoadingState";
import { isNetworkError } from "../utils/network";
import { getMyBookings, getPrescriptions } from "../api/bookings.api";
import { getStaffProfile } from "../api/staff.api";
import { getAssetUrl } from "../api/config";
import { getStaffId } from "../utils/storage";
import {
  CATEGORIES,
  normalizeRecords,
  categoryLabel,
  formatRecordDate,
  countByCategory,
  filterAndSortRecords,
} from "../utils/medicalRecords";
import { toast } from "../Components/Toast";

const filterPillClass = (active) =>
  active
    ? "bg-primary text-white shadow-sm"
    : "bg-surface-lowest text-on-primary-fixed-var border border-primary-fixed-dim/50 hover:bg-surface-low";

const getFullUrl = (fileUrl) => {
  if (!fileUrl) return null;
  if (fileUrl.startsWith("http")) return fileUrl;
  return getAssetUrl(fileUrl);
};

const downloadFile = async (fileUrl, fileName) => {
  const fullUrl = getFullUrl(fileUrl);
  if (!fullUrl) return;
  try {
    const response = await fetch(fullUrl);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "record";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch {
    toast.error("Error downloading file. Please try again.");
  }
};

const RecordCard = ({ record, menuOpen, onToggleMenu, onCloseMenu }) => {
  const isPdf = record.fileType === "pdf";
  const borderAccent = isPdf ? "lg:border-l-red-500" : "lg:border-l-primary";

  const openRecord = () => window.open(getFullUrl(record.fileUrl), "_blank");

  return (
    <div
      className={`relative bg-surface-lowest rounded-2xl border border-surface-container lg:border-l-4 ${borderAccent} p-4 shadow-sm hover:shadow-md transition-shadow`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <span
          className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-md ${
            isPdf ? "bg-red-100 text-red-700" : "bg-primary-fixed/30 text-on-primary-fixed-var"
          }`}
        >
          {isPdf ? "PDF" : record.fileType === "jpg" ? "JPG" : "FILE"}
        </span>
        <div className="relative">
          <button
            type="button"
            onClick={() => onToggleMenu(record.id)}
            className="p-2.5 rounded-full text-on-surface-variant hover:text-on-primary-fixed-var hover:bg-surface-low"
            aria-label="Options"
          >
            <FaEllipsisV size={14} />
          </button>
          {menuOpen === record.id && (
            <>
              <button type="button" className="fixed inset-0 z-10" aria-label="Close menu" onClick={onCloseMenu} />
              <div className="absolute right-0 top-10 z-20 bg-surface-lowest border border-surface-container rounded-xl shadow-lg py-1 min-w-[140px]">
                <button
                  type="button"
                  onClick={() => {
                    onCloseMenu();
                    downloadFile(record.fileUrl, record.title);
                  }}
                  className="w-full px-4 py-2.5 text-left text-sm hover:bg-surface-low flex items-center gap-2"
                >
                  <FaDownload size={12} /> Download
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <h3 className="font-bold text-on-primary-fixed-var text-base leading-snug break-words mb-1">
        {record.title}
      </h3>
      <p className="text-sm text-on-surface-variant leading-snug break-words mb-4">
        {categoryLabel(record.category)} • {record.provider}
      </p>

      {/* Desktop footer — inline View (screenshot) */}
      <div className="hidden lg:flex items-center justify-between pt-3 border-t border-surface-container">
        <span className="text-xs text-on-surface-variant">{formatRecordDate(record.date)}</span>
        <button
          type="button"
          onClick={openRecord}
          className="text-sm font-semibold text-primary flex items-center gap-1 hover:underline"
        >
          <FaEye size={12} /> View
        </button>
      </div>

      {/* Mobile footer — full-width CTA */}
      <div className="lg:hidden border-t border-surface-container/60 pt-3 space-y-3">
        <span className="block text-xs text-on-surface-variant">{formatRecordDate(record.date)}</span>
        <button
          type="button"
          onClick={openRecord}
          className="w-full min-h-[48px] bg-primary text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-primary-container transition-colors"
        >
          View Document
          <FaChevronRight size={12} />
        </button>
      </div>
    </div>
  );
};

const CategoryPills = ({ category, setCategory, counts }) => (
  <div className="relative mt-4">
    <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {CATEGORIES.map(({ id, label }) => (
        <button
          key={id}
          type="button"
          onClick={() => setCategory(id)}
          className={`shrink-0 snap-start inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium min-h-[44px] transition-colors ${filterPillClass(category === id)}`}
        >
          {label}
          <span className={`text-xs px-2 py-0.5 rounded-full ${category === id ? "bg-white/20" : "bg-surface-container text-on-surface-variant"}`}>
            {counts[id] ?? 0}
          </span>
        </button>
      ))}
    </div>
    <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-surface-low to-transparent" />
    <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-surface-low to-transparent" />
  </div>
);

const MedicalRecordsPage = () => {
  const navigate = useNavigate();
  const staffId = getStaffId();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [networkError, setNetworkError] = useState(false);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("date-desc");
  const [menuOpen, setMenuOpen] = useState(null);

  const loadRecords = useCallback(() => {
    if (!staffId) {
      setError("Staff ID not found.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setNetworkError(false);
    setError("");

    Promise.all([
      getMyBookings(staffId).catch(() => ({ data: { bookings: [] } })),
      getStaffProfile(staffId).catch(() => ({ data: {} })),
      getPrescriptions().catch(() => ({ data: { appointments: [] } })),
    ])
      .then(([bookingsRes, profileRes, rxRes]) => {
        const bookings = bookingsRes.data?.success ? bookingsRes.data.bookings || [] : [];
        const staffProfile = profileRes.data?.staff || profileRes.data || null;
        const prescriptionAppointments = rxRes.data?.appointments || [];
        setRecords(normalizeRecords({ bookings, staffProfile, prescriptionAppointments }));
      })
      .catch((err) => {
        if (isNetworkError(err)) setNetworkError(true);
        else setError("Error loading medical records.");
      })
      .finally(() => setLoading(false));
  }, [staffId]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const counts = useMemo(() => countByCategory(records), [records]);
  const filtered = useMemo(
    () => filterAndSortRecords(records, { category, search, sort }),
    [records, category, search, sort]
  );

  return (
    <div className="bg-surface-low min-h-screen flex flex-col">
      <Header />
      <div className="flex flex-col min-h-screen pb-16 lg:pb-0 flex-1">
        <main className="flex-1 max-w-2xl lg:max-w-7xl mx-auto w-full px-3 sm:px-4 py-4 sm:py-8">
          <div className="sticky top-0 z-20 bg-surface-low -mx-3 px-3 pt-2 pb-3 border-b border-surface-container/60 lg:static lg:mx-0 lg:px-0 lg:border-0 lg:pb-0">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-on-primary-fixed-var">
                  Medical Records Vault
                </h1>
                <p className="text-xs sm:text-sm text-on-surface-variant mt-0.5 font-normal">
                  Securely manage and access your health documents.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate("/medicalrecord/upload")}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary text-white px-5 py-2.5 min-h-[44px] rounded-xl font-semibold hover:bg-primary-container transition shrink-0"
              >
                <FaCloudUploadAlt size={16} />
                Upload New Record
              </button>
            </div>

            {!networkError && !loading && records.length > 0 && (
              <div className="lg:hidden">
                <CategoryPills category={category} setCategory={setCategory} counts={counts} />
              </div>
            )}
          </div>

          {error && (
            <p className="text-error text-sm mt-4 mb-4 bg-error-container/30 border border-error/20 rounded-lg px-4 py-2">
              {error}
            </p>
          )}

          {networkError && <OfflineState onRetry={loadRecords} />}

          {!networkError && loading ? (
            <LoadingState branded message="Loading records…" />
          ) : !networkError && records.length === 0 ? (
            <MedicalRecordsEmptyState />
          ) : !networkError ? (
            <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-6 lg:gap-8 mt-4">
              <aside className="hidden lg:block bg-surface-lowest rounded-xl border border-surface-container p-4 h-fit">
                <p className="text-xs font-semibold text-on-surface-variant uppercase tracking-wide mb-3">
                  Categories
                </p>
                <nav className="space-y-1">
                  {CATEGORIES.map(({ id, label }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setCategory(id)}
                      className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-medium transition-colors min-h-[44px] ${
                        category === id
                          ? "bg-primary-fixed/30 text-primary"
                          : "text-on-primary-fixed-var hover:bg-surface-container/50"
                      }`}
                    >
                      <span>{label}</span>
                      <span className="text-xs bg-surface-container text-on-surface-variant px-2 py-0.5 rounded-full">
                        {counts[id] ?? 0}
                      </span>
                    </button>
                  ))}
                </nav>
              </aside>

              <div>
                <div className="flex flex-col sm:flex-row gap-3 mb-2">
                  <div className="relative flex-1">
                    <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant" size={14} />
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search records..."
                      className="w-full pl-11 pr-4 py-2.5 min-h-[44px] border border-surface-container rounded-xl bg-surface-lowest text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <label htmlFor="sort-records" className="hidden sm:inline text-sm text-on-surface-variant whitespace-nowrap">
                      Sort by:
                    </label>
                    <select
                      id="sort-records"
                      value={sort}
                      onChange={(e) => setSort(e.target.value)}
                      className="w-full sm:w-auto min-h-[44px] border border-surface-container rounded-xl px-3 py-2.5 text-sm bg-surface-lowest text-on-primary-fixed-var"
                      aria-label="Sort records"
                    >
                      <option value="date-desc">Date (Newest)</option>
                      <option value="date-asc">Date (Oldest)</option>
                      <option value="name">Name (A–Z)</option>
                    </select>
                  </div>
                </div>
                <p className="text-xs text-on-surface-variant text-right mb-4">
                  {filtered.length} record{filtered.length !== 1 ? "s" : ""}
                </p>

                {filtered.length === 0 ? (
                  <EmptyState
                    title="No matching records"
                    description="Try adjusting your search or category filter to find what you're looking for."
                  />
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filtered.map((record) => (
                      <RecordCard
                        key={record.id}
                        record={record}
                        menuOpen={menuOpen}
                        onToggleMenu={(id) => setMenuOpen(menuOpen === id ? null : id)}
                        onCloseMenu={() => setMenuOpen(null)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default MedicalRecordsPage;
